// v10/src/components/admin/AdminTurnos.js
// v1.3 — con i18n

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Plus, Check, X, Clock, Plane, User,
  ChevronLeft, ChevronRight, AlertCircle, Loader,
  CheckCircle, XCircle, FileText, Trash2,
  ChevronDown, ChevronUp, GraduationCap, ShieldCheck, Stethoscope, MessageSquare,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/apiService';
import useIsMobile from '../../hooks/useIsMobile';

const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const staggerItem = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const SLOT_TYPE_COLORS = {
  instruction: { bg:'rgba(37,99,235,0.12)',  border:'#2563eb', text:'#2563eb', gradient:'linear-gradient(135deg,#2563eb,#3b82f6)' },
  checkride:   { bg:'rgba(124,58,237,0.12)', border:'#7c3aed', text:'#7c3aed', gradient:'linear-gradient(135deg,#7c3aed,#8b5cf6)' },
  solo:        { bg:'rgba(22,163,74,0.12)',   border:'#16a34a', text:'#16a34a', gradient:'linear-gradient(135deg,#16a34a,#22c55e)' },
};
const STATUS_COLORS = {
  pending:   { bg:'rgba(245,158,11,0.1)', border:'rgba(245,158,11,0.3)', text:'#f59e0b' },
  confirmed: { bg:'rgba(34,197,94,0.1)',  border:'rgba(34,197,94,0.3)',  text:'#22c55e' },
  cancelled: { bg:'rgba(239,68,68,0.1)',  border:'rgba(239,68,68,0.3)',  text:'#ef4444' },
  completed: { bg:'rgba(99,102,241,0.1)', border:'rgba(99,102,241,0.3)', text:'#6366f1' },
};

const formatDate = (d) => {
  if (!d) return '—';
  const parts = String(d).split('T')[0].split('-');
  if (parts.length !== 3) return String(d);
  const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  return date.toLocaleDateString('es-AR', { weekday:'short', day:'numeric', month:'short' });
};
const formatTime = (t) => t ? t.substring(0,5) : '';

const TimeInput = ({ label, value, onChange, required, darkMode, sub, text }) => {
  const [confirmed, setConfirmed] = useState(!!value);
  const inputRef = useRef(null);
  const handleChange = (e) => { onChange(e.target.value); setConfirmed(false); };
  const handleConfirm = () => { if (value) { setConfirmed(true); inputRef.current?.blur(); } };
  return (
    <div>
      <span style={{ fontSize:12, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>{label} {required&&'*'}</span>
      <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
        <div style={{ position:'relative', flex:1 }}>
          <input ref={inputRef} type="time" value={value} onChange={handleChange} required={required}
            style={{ ...inputStyle(darkMode,text), marginTop:0, paddingRight:confirmed?'36px':'14px', borderColor:confirmed?'rgba(34,197,94,0.4)':(darkMode?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.08)') }}/>
          {confirmed && <div style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)' }}><CheckCircle style={{ width:16, height:16, color:'#22c55e' }}/></div>}
        </div>
        <motion.button type="button" onClick={handleConfirm} disabled={!value} whileTap={value?{ scale:0.95 }:{}}
          style={{ padding:'10px 14px', borderRadius:12, border:'none', cursor:value?'pointer':'not-allowed', fontFamily:'inherit', fontSize:12, fontWeight:700, whiteSpace:'nowrap', background:confirmed?'rgba(34,197,94,0.12)':value?'linear-gradient(135deg,#3b82f6,#6366f1)':(darkMode?'rgba(255,255,255,0.04)':'#f1f5f9'), color:confirmed?'#22c55e':value?'white':sub, transition:'all 0.2s' }}>
          {confirmed?'✓ Ok':'Establecer'}
        </motion.button>
      </div>
    </div>
  );
};

const SuccessModal = ({ message, onClose, darkMode }) => {
  const { t } = useTranslation('common');
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(8px)' }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.85, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.85, y:20 }}
        transition={{ type:'spring', damping:20, stiffness:280 }}
        style={{ width:'100%', maxWidth:340, borderRadius:24, overflow:'hidden', background:darkMode?'rgba(17,24,39,0.97)':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.06)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ height:4, background:'linear-gradient(90deg,#22c55e,#16a34a)' }}/>
        <div style={{ padding:32, textAlign:'center' }}>
          <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ type:'spring', stiffness:300, damping:18, delay:0.1 }}
            style={{ width:72, height:72, borderRadius:'50%', background:'rgba(34,197,94,0.12)', border:'2px solid rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
            <CheckCircle style={{ width:36, height:36, color:'#22c55e' }}/>
          </motion.div>
          <h3 style={{ margin:'0 0 8px', fontSize:18, fontWeight:800, color:darkMode?'white':'#0f172a' }}>¡Listo!</h3>
          <p style={{ margin:'0 0 24px', fontSize:14, color:darkMode?'rgba(255,255,255,0.5)':'#64748b', lineHeight:1.5 }}>{message}</p>
          <motion.button onClick={onClose} whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
            style={{ width:'100%', padding:'13px', borderRadius:14, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700, color:'white', background:'linear-gradient(135deg,#22c55e,#16a34a)', boxShadow:'0 4px 14px rgba(34,197,94,0.3)' }}>
            Entendido
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const canSeeStudentSection = (booking, currentUser) => (
  booking?.slot_type === 'instruction'
  && currentUser?.role !== 'pilot'
  && currentUser?.id !== booking?.pilot_id
  && !!booking?.pilot_id
);

const AdminTurnos = ({ darkMode, userRole, userId }) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();
  const currentUser = useMemo(() => ({ id: userId, role: userRole }), [userId, userRole]);
  const [tab, setTab]                   = useState('slots');
  const [slots, setSlots]               = useState([]);
  const [bookings, setBookings]         = useState([]);
  const [upcoming, setUpcoming]         = useState([]);
  const [aircraft, setAircraft]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [editSlot, setEditSlot]         = useState(null);
  const [successModal, setSuccessModal] = useState('');
  const [cancelModal, setCancelModal]   = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [completeModal, setCompleteModal]   = useState(null);
  const [completeNotes, setCompleteNotes]   = useState('');
  const [slotForm, setSlotForm]         = useState({ date:'', start_time:'', end_time:'', aircraft_id:'', slot_type:'instruction', notes:'' });
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [weekOffset, setWeekOffset]     = useState(0);

  const SLOT_TYPE_LABELS = { instruction:t('slot_instruction'), checkride:t('slot_checkride'), solo:t('slot_solo') };
  const STATUS_LABELS    = { pending:t('status_pending'), confirmed:t('status_confirmed'), cancelled:t('status_cancelled'), completed:t('status_completed') };

  const text    = darkMode ? 'white' : '#0f172a';
  const sub     = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const cardBg  = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)';
  const border  = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const glassBg = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [slotsRes, bookingsRes, upcomingRes, acRes] = await Promise.all([
        api.get('/api/slots/my'),
        api.get('/api/bookings'),
        api.get('/api/bookings/upcoming'),
        api.get('/api/aircraft'),
      ]);
      setSlots(slotsRes.data.data || []);
      setBookings(bookingsRes.data.data || []);
      setUpcoming(upcomingRes.data.data || []);
      const rawAircraft = acRes.data.data || acRes.data || [];
      setAircraft(Array.isArray(rawAircraft) ? rawAircraft : []);
    } catch (err) { console.error('Error fetching turnos:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60*1000);
    return () => clearInterval(interval);
  }, []);

  const showError = (msg) => { setError(msg); setTimeout(()=>setError(''), 4000); };

  const handleEditSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.date || !slotForm.start_time || !slotForm.end_time) { showError('Completá fecha, hora inicio y hora fin'); return; }
    setSaving(true);
    try {
      await api.put(`/api/slots/${editSlot.id}`, slotForm);
      setShowModal(false); setEditSlot(null);
      setSlotForm({ date:'', start_time:'', end_time:'', aircraft_id:'', slot_type:'instruction', notes:'' });
      setSuccessModal('Slot actualizado correctamente.'); fetchData();
    } catch (err) { showError(err.response?.data?.message || 'Error al actualizar slot'); }
    finally { setSaving(false); }
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.date || !slotForm.start_time || !slotForm.end_time) { showError('Completá fecha, hora inicio y hora fin'); return; }
    setSaving(true);
    try {
      await api.post('/api/slots', slotForm);
      setShowModal(false);
      setSlotForm({ date:'', start_time:'', end_time:'', aircraft_id:'', slot_type:'instruction', notes:'' });
      setSuccessModal('Slot de disponibilidad creado correctamente.'); fetchData();
    } catch (err) { showError(err.response?.data?.message || 'Error al crear slot'); }
    finally { setSaving(false); }
  };

  const handleDeleteSlot = async (id) => {
    try { await api.delete(`/api/slots/${id}`); setSuccessModal('Slot eliminado correctamente.'); fetchData(); }
    catch (err) { showError(err.response?.data?.message || 'Error al eliminar'); }
  };

  const handleConfirm = async (id) => {
    try { await api.put(`/api/bookings/${id}/confirm`); setSuccessModal('Turno confirmado. Se notificó al piloto.'); fetchData(); }
    catch (err) { showError(err.response?.data?.message || 'Error al confirmar'); }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    try {
      await api.put(`/api/bookings/${cancelModal}/cancel`, { reason:cancelReason });
      setCancelModal(null); setCancelReason('');
      setSuccessModal('Turno cancelado. Se notificó al piloto.'); fetchData();
    } catch (err) { showError(err.response?.data?.message || 'Error al cancelar'); }
  };

  const handleComplete = (booking) => {
    setCompleteNotes('');
    setCompleteModal(booking);
  };

  const confirmComplete = async () => {
    if (!completeModal) return;
    setSaving(true);
    try {
      const trimmed = (completeNotes || '').trim();
      await api.put(`/api/bookings/${completeModal.id}/complete`, trimmed ? { instructor_notes: trimmed } : {});
      setCompleteModal(null); setCompleteNotes('');
      setSuccessModal('Turno marcado como completado.'); fetchData();
    } catch (err) { showError(err.response?.data?.message || 'Error al completar'); }
    finally { setSaving(false); }
  };

  const weekDays = useMemo(() => {
    const today  = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1 + weekOffset*7);
    return Array.from({ length:7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate()+i); return d; });
  }, [weekOffset]);

  const pendingBookings   = bookings.filter(b => b.status === 'pending');
  const availableAircraft = aircraft.filter(a => a.is_active !== false);

  const tabs = [
    { id:'slots',    label:t('tab_calendar'), count:slots.length },
    { id:'pending',  label:t('tab_pending'),  count:pendingBookings.length },
    { id:'upcoming', label:t('tab_upcoming'), count:upcoming.length },
    { id:'all',      label:t('tab_history'),  count:bookings.length },
  ];

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:300 }}>
      <Loader style={{ width:32, height:32, animation:'spin 1s linear infinite', color:sub }}/>
    </div>
  );

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom:28, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ margin:0, fontSize:24, fontWeight:800, color:text, display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:40, height:40, borderRadius:14, background:'linear-gradient(135deg,#3b82f6,#6366f1)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(59,130,246,0.3)' }}>
              <Calendar style={{ width:20, height:20, color:'white' }}/>
            </div>
            {t('turnos_title')}
          </h2>
          <p style={{ margin:'6px 0 0 50px', fontSize:13, color:sub }}>{t('turnos_desc')}</p>
        </div>
        {isMobile && (
          <motion.button onClick={()=>{ setEditSlot(null); setSlotForm({ date:'', start_time:'', end_time:'', aircraft_id:'', slot_type:'instruction', notes:'' }); setShowModal(true); }} whileTap={{ scale:0.95 }}
            style={{ padding:'10px 16px', borderRadius:14, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', display:'flex', alignItems:'center', gap:6, boxShadow:'0 4px 14px rgba(34,197,94,0.3)' }}>
            <Plus style={{ width:16, height:16 }}/> {t('add_availability')}
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
            style={{ marginBottom:16, padding:'12px 16px', borderRadius:16, display:'flex', alignItems:'center', gap:10, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', backdropFilter:'blur(12px)' }}>
            <AlertCircle style={{ width:16, height:16, color:'#ef4444', flexShrink:0 }}/>
            <span style={{ fontSize:13, fontWeight:600, color:'#ef4444' }}>{error}</span>
            <button onClick={()=>setError('')} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#ef4444' }}><X style={{ width:14, height:14 }}/></button>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display:'flex', gap:6, marginBottom:24, overflowX:'auto', WebkitOverflowScrolling:'touch', flexWrap:'nowrap', paddingBottom:4, background:glassBg, borderRadius:16, padding:5, border, backdropFilter:'blur(12px)' }}>
        {tabs.map(tabItem => (
          <motion.button key={tabItem.id} onClick={()=>setTab(tabItem.id)} whileTap={{ scale:0.97 }}
            style={{ padding:isMobile?'10px 14px':'10px 18px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, whiteSpace:'nowrap', flexShrink:0, transition:'all 0.2s ease', background:tab===tabItem.id?'linear-gradient(135deg,#3b82f6,#6366f1)':'transparent', color:tab===tabItem.id?'white':sub, boxShadow:tab===tabItem.id?'0 4px 12px rgba(59,130,246,0.25)':'none' }}>
            {tabItem.label}
            {tabItem.count > 0 && (
              <span style={{ marginLeft:6, padding:'2px 7px', borderRadius:8, fontSize:10, fontWeight:800, background:tab===tabItem.id?'rgba(255,255,255,0.25)':(tabItem.id==='pending'&&tabItem.count>0?'rgba(245,158,11,0.15)':'rgba(59,130,246,0.12)'), color:tab===tabItem.id?'white':(tabItem.id==='pending'&&tabItem.count>0?'#f59e0b':'#3b82f6') }}>
                {tabItem.count}
              </span>
            )}
          </motion.button>
        ))}
      </div>

      {/* ── Calendario ── */}
      {tab === 'slots' && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
            <motion.button onClick={()=>setWeekOffset(w=>w-1)} whileTap={{ scale:0.92 }}
              style={{ width:36, height:36, borderRadius:10, border, background:glassBg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:sub, backdropFilter:'blur(8px)' }}>
              <ChevronLeft style={{ width:16, height:16 }}/>
            </motion.button>
            <span style={{ fontSize:15, fontWeight:800, color:text, letterSpacing:'-0.3px' }}>
              {weekDays[0].toLocaleDateString('es-AR',{day:'numeric',month:'short'})} — {weekDays[6].toLocaleDateString('es-AR',{day:'numeric',month:'short',year:'numeric'})}
            </span>
            <motion.button onClick={()=>setWeekOffset(w=>w+1)} whileTap={{ scale:0.92 }}
              style={{ width:36, height:36, borderRadius:10, border, background:glassBg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:sub, backdropFilter:'blur(8px)' }}>
              <ChevronRight style={{ width:16, height:16 }}/>
            </motion.button>
            {weekOffset !== 0 && (
              <motion.button onClick={()=>setWeekOffset(0)} whileTap={{ scale:0.95 }}
                style={{ fontSize:12, fontWeight:700, color:'#3b82f6', background:'rgba(59,130,246,0.1)', border:'none', cursor:'pointer', padding:'6px 14px', borderRadius:8 }}>
                Hoy
              </motion.button>
            )}
            <div style={{ flex:1 }}/>
            <div style={{ display:'flex', gap:12, fontSize:11, fontWeight:600, color:sub }}>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{ width:8,height:8,borderRadius:3,background:'#2563eb' }}/> {t('slot_instruction')}</span>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{ width:8,height:8,borderRadius:3,background:'#7c3aed' }}/> {t('slot_checkride')}</span>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{ width:8,height:8,borderRadius:3,background:'#16a34a' }}/> {t('slot_solo')}</span>
            </div>
          </div>

          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch', marginLeft:-4, marginRight:-4, paddingLeft:4, paddingRight:4, paddingBottom:4 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7, minmax(110px, 1fr))', gap:8, minWidth:770 }}>
              {weekDays.map((day, idx) => {
                const dayStr   = day.toISOString().split('T')[0];
                const daySlots = slots.filter(s => s.date && s.date.split('T')[0] === dayStr);
                const isToday  = new Date().toISOString().split('T')[0] === dayStr;
                const isWeekend = day.getDay()===0 || day.getDay()===6;
                return (
                  <motion.div key={dayStr} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:idx*0.04 }}
                    style={{ borderRadius:18, border:isToday?'2px solid rgba(59,130,246,0.4)':border, background:isToday?(darkMode?'rgba(59,130,246,0.06)':'rgba(59,130,246,0.04)'):glassBg, padding:'12px 8px', minHeight:130, backdropFilter:'blur(12px)', opacity:isWeekend?0.6:1, boxShadow:isToday?'0 4px 20px rgba(59,130,246,0.12)':'none' }}>
                    <div style={{ textAlign:'center', marginBottom:8, paddingBottom:8, borderBottom:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.05)' }}>
                      <p style={{ margin:0, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', color:isToday?'#3b82f6':sub }}>{day.toLocaleDateString('es-AR',{weekday:'short'})}</p>
                      <p style={{ margin:'2px 0 0', fontSize:20, fontWeight:800, color:isToday?'#3b82f6':text }}>{day.getDate()}</p>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {daySlots.length === 0 && (
                        <div style={{ borderRadius:10, border:`1.5px dashed ${darkMode?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)'}`, padding:'8px 6px', textAlign:'center' }}>
                          <span style={{ fontSize:9, color:sub, fontWeight:600 }}>{t('no_slots')}</span>
                        </div>
                      )}
                      {daySlots.map(slot => {
                        const tc = SLOT_TYPE_COLORS[slot.slot_type] || SLOT_TYPE_COLORS.instruction;
                        const hasPending   = bookings.some(b=>b.slot_id===slot.id&&b.status==='pending');
                        const hasConfirmed = bookings.some(b=>b.slot_id===slot.id&&b.status==='confirmed');
                        const hasCompleted = bookings.some(b=>b.slot_id===slot.id&&b.status==='completed');
                        let slotBorder;
                        if (hasPending)        slotBorder = '2px solid rgba(249,115,22,0.7)';
                        else if (hasConfirmed) slotBorder = '2px solid #22c55e';
                        else if (hasCompleted) slotBorder = '1.5px solid rgba(99,102,241,0.4)';
                        else if (slot.is_available) slotBorder = `1.5px dashed ${darkMode?'rgba(255,255,255,0.15)':'rgba(0,0,0,0.12)'}`;
                        else slotBorder = `1.5px solid ${tc.border}`;
                        return (
                          <motion.div key={slot.id} initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
                            style={{ padding:'7px 8px', borderRadius:10, background:hasCompleted?(darkMode?'rgba(99,102,241,0.06)':'rgba(99,102,241,0.05)'):tc.bg, border:slotBorder, fontSize:10, position:'relative', overflow:'hidden', animation:hasPending?'pulseOrange 2s ease-in-out infinite':'none', opacity:hasCompleted?0.6:1 }}>
                            <div style={{ position:'absolute', top:0, left:0, width:3, height:'100%', background:hasCompleted?'linear-gradient(180deg,#6366f1,#8b5cf6)':tc.gradient, borderRadius:'3px 0 0 3px' }}/>
                            <div style={{ paddingLeft:6 }}>
                              <div style={{ fontWeight:800, color:hasCompleted?'#6366f1':tc.text, fontSize:11 }}>{formatTime(slot.start_time)}-{formatTime(slot.end_time)}</div>
                              <div style={{ color:sub, fontSize:9, fontWeight:600, marginTop:1 }}>{SLOT_TYPE_LABELS[slot.slot_type]||slot.slot_type}</div>
                              {hasPending   && <span style={{ display:'inline-block', marginTop:3, padding:'1px 5px', borderRadius:4, fontSize:8, fontWeight:700, background:'rgba(249,115,22,0.15)', color:'#f97316' }}>Reserva pendiente</span>}
                              {hasConfirmed && <span style={{ display:'inline-block', marginTop:3, padding:'1px 5px', borderRadius:4, fontSize:8, fontWeight:700, background:'rgba(34,197,94,0.15)', color:'#22c55e' }}>{t('status_confirmed')}</span>}
                              {hasCompleted && <span style={{ display:'inline-block', marginTop:3, padding:'1px 5px', borderRadius:4, fontSize:8, fontWeight:700, background:'rgba(99,102,241,0.15)', color:'#6366f1' }}>{t('status_completed')}</span>}
                            </div>
                            <div style={{ borderTop:`1px solid ${tc.border}25`, marginTop:6, paddingTop:5, display:'flex', flexDirection:'column', gap:3 }}>
                              <motion.button onClick={()=>{ setEditSlot(slot); setSlotForm({ date:slot.date?slot.date.split('T')[0]:'', start_time:slot.start_time||'', end_time:slot.end_time||'', aircraft_id:slot.aircraft_id||'', slot_type:slot.slot_type||'instruction', notes:slot.notes||'' }); setShowModal(true); }} whileTap={{ scale:0.95 }}
                                style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'4px 0', borderRadius:6, border:'none', cursor:'pointer', background:'transparent', color:'#3b82f6', fontSize:9, fontWeight:700, fontFamily:'inherit', opacity:0.7 }}
                                onMouseEnter={e=>e.currentTarget.style.opacity='1'} onMouseLeave={e=>e.currentTarget.style.opacity='0.7'}>
                                ✏️ Editar slot
                              </motion.button>
                              <motion.button onClick={()=>handleDeleteSlot(slot.id)} whileTap={{ scale:0.95 }}
                                style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'4px 0', borderRadius:6, border:'none', cursor:'pointer', background:'transparent', color:'#ef4444', fontSize:9, fontWeight:700, fontFamily:'inherit', opacity:0.7 }}
                                onMouseEnter={e=>e.currentTarget.style.opacity='1'} onMouseLeave={e=>e.currentTarget.style.opacity='0.7'}>
                                <Trash2 style={{ width:10, height:10 }}/> Eliminar slot
                              </motion.button>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === 'pending' && (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {pendingBookings.length === 0 && (
            <motion.div variants={staggerItem} style={{ textAlign:'center', padding:'50px 20px', borderRadius:20, border, background:glassBg, backdropFilter:'blur(12px)' }}>
              <CheckCircle style={{ width:40, height:40, color:sub, marginBottom:12, opacity:0.4 }}/>
              <p style={{ color:sub, fontSize:14, fontWeight:600, margin:0 }}>{t('no_pending')}</p>
            </motion.div>
          )}
          {pendingBookings.map(b => (
            <motion.div key={b.id} variants={staggerItem}>
              <BookingCard booking={b} darkMode={darkMode} text={text} sub={sub} cardBg={cardBg} border={border} glassBg={glassBg} slotTypeLabels={SLOT_TYPE_LABELS} statusLabels={STATUS_LABELS} currentUser={currentUser} t={t}
                onConfirm={()=>handleConfirm(b.id)} onCancel={()=>setCancelModal(b.id)} showActions/>
            </motion.div>
          ))}
        </motion.div>
      )}

      {tab === 'upcoming' && (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {upcoming.length === 0 && (
            <motion.div variants={staggerItem} style={{ textAlign:'center', padding:'50px 20px', borderRadius:20, border, background:glassBg, backdropFilter:'blur(12px)' }}>
              <Calendar style={{ width:40, height:40, color:sub, marginBottom:12, opacity:0.4 }}/>
              <p style={{ color:sub, fontSize:14, fontWeight:600, margin:0 }}>{t('no_upcoming')}</p>
            </motion.div>
          )}
          {upcoming.map(b => (
            <motion.div key={b.id} variants={staggerItem}>
              <BookingCard booking={b} darkMode={darkMode} text={text} sub={sub} cardBg={cardBg} border={border} glassBg={glassBg} slotTypeLabels={SLOT_TYPE_LABELS} statusLabels={STATUS_LABELS} currentUser={currentUser} t={t}
                onComplete={()=>handleComplete(b)} onCancel={()=>setCancelModal(b.id)} showComplete={b.status==='confirmed'}
                onEdit={()=>{ setEditSlot({id:b.slot_id}); setSlotForm({ date:b.date?b.date.split('T')[0]:'', start_time:b.start_time||'', end_time:b.end_time||'', aircraft_id:b.aircraft_id||'', slot_type:b.slot_type||'instruction', notes:b.notes||'' }); setShowModal(true); }} showEdit/>
            </motion.div>
          ))}
        </motion.div>
      )}

      {tab === 'all' && (
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {bookings.length === 0 && (
            <motion.div variants={staggerItem} style={{ textAlign:'center', padding:'50px 20px', borderRadius:20, border, background:glassBg, backdropFilter:'blur(12px)' }}>
              <FileText style={{ width:40, height:40, color:sub, marginBottom:12, opacity:0.4 }}/>
              <p style={{ color:sub, fontSize:14, fontWeight:600, margin:0 }}>{t('no_history')}</p>
            </motion.div>
          )}
          {bookings.map(b => (
            <motion.div key={b.id} variants={staggerItem}>
              <BookingCard booking={b} darkMode={darkMode} text={text} sub={sub} cardBg={cardBg} border={border} glassBg={glassBg} slotTypeLabels={SLOT_TYPE_LABELS} statusLabels={STATUS_LABELS} currentUser={currentUser} t={t}/>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* FAB — desktop only; mobile usa botón inline en header */}
      {!isMobile && (
        <motion.button onClick={()=>setShowModal(true)} whileHover={{ scale:1.08 }} whileTap={{ scale:0.92 }}
          style={{ position:'fixed', bottom:100, right:28, width:56, height:56, borderRadius:18, border:'none', cursor:'pointer', zIndex:50, background:'linear-gradient(135deg,#22c55e,#16a34a)', boxShadow:'0 8px 28px rgba(34,197,94,0.4)', display:'flex', alignItems:'center', justifyContent:'center', color:'white' }}>
          <Plus style={{ width:24, height:24 }}/>
        </motion.button>
      )}

      {/* MODAL: Crear/Editar slot */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(8px)' }}
            onClick={(e)=>{ if(e.target===e.currentTarget){setShowModal(false);setEditSlot(null);setSlotForm({date:'',start_time:'',end_time:'',aircraft_id:'',slot_type:'instruction',notes:''});} }}>
            <motion.div initial={{ scale:0.9, y:20, opacity:0 }} animate={{ scale:1, y:0, opacity:1 }} exit={{ scale:0.9, y:20, opacity:0 }}
              transition={{ type:'spring', damping:25, stiffness:300 }}
              style={{ width:'100%', maxWidth:460, borderRadius:24, overflow:'hidden', background:darkMode?'rgba(17,24,39,0.97)':'rgba(255,255,255,0.97)', backdropFilter:'blur(20px)', border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.06)', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', maxHeight:'90vh', overflowY:'auto' }}>
              <div style={{ height:4, background:'linear-gradient(90deg,#3b82f6,#6366f1,#8b5cf6)' }}/>
              <form onSubmit={editSlot?handleEditSlot:handleCreateSlot} style={{ padding:28 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
                  <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:text, display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:10, background:'linear-gradient(135deg,#3b82f6,#6366f1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Plus style={{ width:18, height:18, color:'white' }}/>
                    </div>
                    {editSlot ? t('edit_slot_title') : t('add_availability')}
                  </h3>
                  <motion.button type="button" onClick={()=>setShowModal(false)} whileTap={{ scale:0.9 }}
                    style={{ width:32, height:32, borderRadius:10, border:'none', cursor:'pointer', flexShrink:0, background:darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', color:sub }}>
                    <X style={{ width:16, height:16 }}/>
                  </motion.button>
                </div>

                <label style={{ display:'block', marginBottom:16 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>Fecha *</span>
                  <input type="date" value={slotForm.date} onChange={e=>setSlotForm(f=>({...f,date:e.target.value}))} style={inputStyle(darkMode,text)} required/>
                </label>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                  <TimeInput label="Hora inicio" value={slotForm.start_time} required onChange={v=>setSlotForm(f=>({...f,start_time:v}))} darkMode={darkMode} sub={sub} text={text}/>
                  <TimeInput label="Hora fin" value={slotForm.end_time} required onChange={v=>setSlotForm(f=>({...f,end_time:v}))} darkMode={darkMode} sub={sub} text={text}/>
                </div>

                <label style={{ display:'block', marginBottom:16 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>Tipo</span>
                  <div style={{ display:'flex', gap:8, marginTop:6 }}>
                    {Object.entries(SLOT_TYPE_LABELS).map(([key, label]) => {
                      const tc = SLOT_TYPE_COLORS[key];
                      const active = slotForm.slot_type === key;
                      return (
                        <motion.button key={key} type="button" whileTap={{ scale:0.95 }}
                          onClick={()=>setSlotForm(f=>({...f,slot_type:key}))}
                          style={{ flex:1, padding:'10px 8px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, transition:'all 0.2s ease', background:active?tc.gradient:(darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'), color:active?'white':sub, boxShadow:active?`0 4px 12px ${tc.border}33`:'none' }}>
                          {label}
                        </motion.button>
                      );
                    })}
                  </div>
                </label>

                <label style={{ display:'block', marginBottom:16 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>Aeronave</span>
                  <select value={slotForm.aircraft_id} onChange={e=>setSlotForm(f=>({...f,aircraft_id:e.target.value}))} style={inputStyle(darkMode,text)}>
                    <option value="">Sin asignar</option>
                    {availableAircraft.map(a => <option key={a.id} value={a.id}>{a.manufacturer} {a.model_name}{a.registration?` (${a.registration})`:''}</option>)}
                  </select>
                  {availableAircraft.length===0 && <p style={{ margin:'4px 0 0', fontSize:11, color:'#f59e0b' }}>No se encontraron aeronaves — podés dejar Sin asignar</p>}
                </label>

                <label style={{ display:'block', marginBottom:24 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>Notas</span>
                  <textarea value={slotForm.notes} onChange={e=>setSlotForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ ...inputStyle(darkMode,text), resize:'vertical' }} placeholder="Opcional"/>
                </label>

                <div style={{ display:'flex', gap:10 }}>
                  <motion.button type="button" onClick={()=>setShowModal(false)} whileTap={{ scale:0.97 }}
                    style={{ flex:1, padding:14, borderRadius:14, border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.08)', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:600, color:sub }}>
                    Cancelar
                  </motion.button>
                  <motion.button type="submit" disabled={saving} whileTap={{ scale:0.97 }}
                    style={{ flex:1, padding:14, borderRadius:14, border:'none', cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700, background:'linear-gradient(135deg,#3b82f6,#6366f1)', color:'white', boxShadow:'0 4px 16px rgba(59,130,246,0.3)', opacity:saving?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    {saving ? <><motion.div animate={{ rotate:360 }} transition={{ duration:1, repeat:Infinity, ease:'linear' }} style={{ width:16, height:16, borderRadius:'50%', border:'2.5px solid transparent', borderTopColor:'white' }}/>Guardando...</> : editSlot ? t('save_changes') : t('create_slot')}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL: Cancelar turno */}
      <AnimatePresence>
        {cancelModal && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(8px)' }}
            onClick={(e)=>{ if(e.target===e.currentTarget){setCancelModal(null);setCancelReason('');} }}>
            <motion.div initial={{ scale:0.9, y:20, opacity:0 }} animate={{ scale:1, y:0, opacity:1 }} exit={{ scale:0.9, y:20, opacity:0 }}
              transition={{ type:'spring', damping:25, stiffness:300 }}
              style={{ width:'100%', maxWidth:400, borderRadius:24, overflow:'hidden', background:darkMode?'rgba(17,24,39,0.97)':'rgba(255,255,255,0.97)', backdropFilter:'blur(20px)', border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.06)', boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ height:4, background:'linear-gradient(90deg,#ef4444,#f97316)' }}/>
              <div style={{ padding:28 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:text, display:'flex', alignItems:'center', gap:8 }}>
                    <XCircle style={{ width:20, height:20, color:'#ef4444' }}/> {t('cancel_slot_title')}
                  </h3>
                  <motion.button type="button" onClick={()=>{setCancelModal(null);setCancelReason('');}} whileTap={{ scale:0.9 }}
                    style={{ width:32, height:32, borderRadius:10, border:'none', cursor:'pointer', background:darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', color:sub }}>
                    <X style={{ width:16, height:16 }}/>
                  </motion.button>
                </div>
                <p style={{ margin:'0 0 16px', fontSize:13, color:sub }}>Esta acción notificará al piloto</p>
                <textarea value={cancelReason} onChange={e=>setCancelReason(e.target.value)} rows={3} style={inputStyle(darkMode,text)} placeholder="Motivo de la cancelación (opcional)"/>
                <div style={{ display:'flex', gap:10, marginTop:20 }}>
                  <motion.button onClick={()=>{setCancelModal(null);setCancelReason('');}} whileTap={{ scale:0.97 }}
                    style={{ flex:1, padding:14, borderRadius:14, border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.08)', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:600, color:sub }}>
                    Volver
                  </motion.button>
                  <motion.button onClick={handleCancel} whileTap={{ scale:0.97 }}
                    style={{ flex:1, padding:14, borderRadius:14, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700, background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'white', boxShadow:'0 4px 16px rgba(239,68,68,0.3)' }}>
                    Confirmar cancelación
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL: Completar turno con notas pedagógicas */}
      <AnimatePresence>
        {completeModal && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(8px)' }}
            onClick={(e)=>{ if(e.target===e.currentTarget){ setCompleteModal(null); setCompleteNotes(''); } }}>
            <motion.div initial={{ scale:0.9, y:20, opacity:0 }} animate={{ scale:1, y:0, opacity:1 }} exit={{ scale:0.9, y:20, opacity:0 }}
              transition={{ type:'spring', damping:25, stiffness:300 }}
              style={{ width:'100%', maxWidth:460, borderRadius:24, overflow:'hidden', background:darkMode?'rgba(17,24,39,0.97)':'rgba(255,255,255,0.97)', backdropFilter:'blur(20px)', border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.06)', boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ height:4, background:'linear-gradient(90deg,#6366f1,#8b5cf6)' }}/>
              <div style={{ padding:28 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:text, display:'flex', alignItems:'center', gap:8 }}>
                    <CheckCircle style={{ width:20, height:20, color:'#6366f1' }}/> {t('student_complete_with_notes')}
                  </h3>
                  <motion.button type="button" onClick={()=>{ setCompleteModal(null); setCompleteNotes(''); }} whileTap={{ scale:0.9 }}
                    style={{ width:32, height:32, borderRadius:10, border:'none', cursor:'pointer', background:darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', color:sub }}>
                    <X style={{ width:16, height:16 }}/>
                  </motion.button>
                </div>
                <p style={{ margin:'0 0 6px', fontSize:13, fontWeight:700, color:text, marginTop:14 }}>{t('student_pedagogical_notes')}</p>
                <p style={{ margin:'0 0 10px', fontSize:12, color:sub }}>{t('student_pedagogical_notes_help')}</p>
                <textarea value={completeNotes} onChange={e=>setCompleteNotes(e.target.value)} rows={4} style={{ ...inputStyle(darkMode,text), resize:'vertical' }} placeholder={t('student_pedagogical_placeholder')}/>
                <div style={{ display:'flex', gap:10, marginTop:20 }}>
                  <motion.button onClick={()=>{ setCompleteModal(null); setCompleteNotes(''); }} whileTap={{ scale:0.97 }}
                    style={{ flex:1, padding:14, borderRadius:14, border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.08)', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:600, color:sub }}>
                    Cancelar
                  </motion.button>
                  <motion.button onClick={confirmComplete} disabled={saving} whileTap={{ scale:0.97 }}
                    style={{ flex:1, padding:14, borderRadius:14, border:'none', cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700, background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'white', boxShadow:'0 4px 16px rgba(99,102,241,0.3)', opacity:saving?0.7:1 }}>
                    {saving ? 'Guardando...' : 'Confirmar'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {successModal && <SuccessModal message={successModal} darkMode={darkMode} onClose={()=>setSuccessModal('')}/>}
      </AnimatePresence>

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulseOrange { 0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.4); } 50% { box-shadow: 0 0 0 4px rgba(249,115,22,0.15); } }
      `}</style>
    </div>
  );
};

const inputStyle = (darkMode, text) => ({
  width:'100%', padding:'12px 14px', marginTop:6, boxSizing:'border-box',
  background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.02)',
  border:darkMode?'1.5px solid rgba(255,255,255,0.1)':'1.5px solid rgba(0,0,0,0.08)',
  borderRadius:12, fontSize:14, color:text, outline:'none', fontFamily:'inherit',
  backdropFilter:'blur(8px)', transition:'border-color 0.15s ease',
});

const expiryColors = (days) => {
  if (days == null) return { bg:'rgba(148,163,184,0.12)', border:'rgba(148,163,184,0.3)', text:'#94a3b8' };
  if (days < 30)    return { bg:'rgba(239,68,68,0.12)',   border:'rgba(239,68,68,0.35)',  text:'#ef4444' };
  if (days < 90)    return { bg:'rgba(245,158,11,0.12)',  border:'rgba(245,158,11,0.35)', text:'#f59e0b' };
  return { bg:'rgba(34,197,94,0.12)', border:'rgba(34,197,94,0.35)', text:'#22c55e' };
};

const formatDateShort = (d) => {
  if (!d) return '—';
  const parts = String(d).split('T')[0].split('-');
  if (parts.length !== 3) return String(d);
  const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  return date.toLocaleDateString('es-AR', { day:'numeric', month:'short', year:'numeric' });
};

const StudentSection = ({ booking, darkMode, text, sub, t }) => {
  const isMobile = useIsMobile();
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [fetchError, setFetchError]     = useState(false);
  const [hidden, setHidden]             = useState(false);
  const [expanded, setExpanded]         = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const bookingId     = booking.id;
  const bookingStatus = booking.status;

  // Mount + re-fetch al pasar a 'completed'. Trae datos y aplica heurística auto-expand.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(false);
    (async () => {
      try {
        const res = await api.get(`/api/bookings/${bookingId}/student-context`);
        if (cancelled) return;
        const payload = res.data?.data || null;
        setData(payload);
        const s   = payload?.student || {};
        const rn  = payload?.recent_notes?.[0];
        const lic = s.license_expiry_days;
        const med = s.medical_expiry_days;
        const noteRecent = rn?.flight_date
          ? (Date.now() - new Date(rn.flight_date).getTime()) <= 7 * 24 * 60 * 60 * 1000
          && (Date.now() - new Date(rn.flight_date).getTime()) >= 0
          : false;
        const shouldAutoExpand =
          (lic != null && lic < 30) ||
          (med != null && med < 30) ||
          noteRecent;
        if (shouldAutoExpand) setExpanded(true);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 403 || status === 404) setHidden(true);
        else setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId, bookingStatus]);

  if (hidden) return null;

  const headerBg = darkMode ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.05)';
  const divider  = darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)';

  const student     = data?.student;
  const stats       = data?.stats;
  const recentNotes = data?.recent_notes || [];
  const latestNote  = recentNotes[0];
  const olderNotes  = recentNotes.slice(1);

  return (
    <div style={{ marginTop:12, borderRadius:14, border:`1px solid ${darkMode?'rgba(99,102,241,0.18)':'rgba(99,102,241,0.18)'}`, background:headerBg, overflow:'hidden' }}>
      <button type="button" onClick={()=>setExpanded(v=>!v)}
        style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'transparent', border:'none', cursor:'pointer', fontFamily:'inherit', color:text, textAlign:'left' }}>
        <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#6366f1,#8b5cf6)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <GraduationCap style={{ width:14, height:14, color:'white' }}/>
        </div>
        <span style={{ fontSize:13, fontWeight:800, letterSpacing:'-0.2px' }}>{t('student_section_title')}</span>
        {student?.name && <span style={{ fontSize:12, color:sub, fontWeight:600 }}>· {student.name}</span>}
        <div style={{ flex:1 }}/>
        {expanded ? <ChevronUp style={{ width:14, height:14, color:sub }}/> : <ChevronDown style={{ width:14, height:14, color:sub }}/>}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
            style={{ borderTop:divider }}>
            <div style={{ padding:14 }}>
              {loading && !data && (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:sub }}>
                  <Loader style={{ width:14, height:14, animation:'spin 1s linear infinite' }}/> Cargando…
                </div>
              )}

              {fetchError && (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#ef4444' }}>
                  <AlertCircle style={{ width:14, height:14 }}/> {t('student_context_error')}
                </div>
              )}

              {data && (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {/* Stats */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:8 }}>
                    <div style={{ padding:'10px 12px', borderRadius:10, background:darkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.02)' }}>
                      <div style={{ fontSize:18, fontWeight:800, color:text }}>{stats?.total_hours != null ? Number(stats.total_hours).toFixed(1) : '0.0'}</div>
                      <div style={{ fontSize:10, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>{t('student_total_hours')}</div>
                    </div>
                    <div style={{ padding:'10px 12px', borderRadius:10, background:darkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.02)' }}>
                      <div style={{ fontSize:18, fontWeight:800, color:text }}>{stats?.total_flights ?? 0}</div>
                      <div style={{ fontSize:10, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>{t('student_total_flights')}</div>
                    </div>
                    <div style={{ padding:'10px 12px', borderRadius:10, background:darkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.02)' }}>
                      <div style={{ fontSize:13, fontWeight:800, color:text }}>
                        {stats?.last_flight_date ? formatDateShort(stats.last_flight_date) : t('student_no_flights')}
                      </div>
                      <div style={{ fontSize:10, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                        {t('student_last_flight')}
                        {stats?.days_since_last_flight != null && ` · ${t('student_days_ago', { days: stats.days_since_last_flight })}`}
                      </div>
                    </div>
                  </div>

                  {/* Documentación */}
                  <div style={{ display:'flex', flexDirection:isMobile?'column':'row', gap:8, flexWrap:'wrap', alignItems:isMobile?'stretch':'center' }}>
                    {(() => {
                      const c = expiryColors(student?.license_expiry_days);
                      const days = student?.license_expiry_days;
                      const label = days == null ? t('student_no_data') : days < 0 ? t('student_expired') : t('student_expires_in', { days });
                      return (
                        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:10, background:c.bg, border:`1px solid ${c.border}`, minWidth:0 }}>
                          <ShieldCheck style={{ width:13, height:13, color:c.text, flexShrink:0 }}/>
                          <span style={{ fontSize:11, fontWeight:700, color:c.text, wordBreak:'break-word' }}>{t('student_license_label')}: {label}</span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const c = expiryColors(student?.medical_expiry_days);
                      const days = student?.medical_expiry_days;
                      const label = days == null ? t('student_no_data') : days < 0 ? t('student_expired') : t('student_expires_in', { days });
                      return (
                        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:10, background:c.bg, border:`1px solid ${c.border}`, minWidth:0 }}>
                          <Stethoscope style={{ width:13, height:13, color:c.text, flexShrink:0 }}/>
                          <span style={{ fontSize:11, fontWeight:700, color:c.text, wordBreak:'break-word' }}>{t('student_medical_label')}: {label}</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Notas */}
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                      <MessageSquare style={{ width:13, height:13, color:sub }}/>
                      <span style={{ fontSize:11, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:'0.5px' }}>{t('student_recent_note_title')}</span>
                    </div>
                    {!latestNote && (
                      <p style={{ margin:0, fontSize:12, color:sub, fontStyle:'italic' }}>{t('student_no_notes')}</p>
                    )}
                    {latestNote && (
                      <div style={{ padding:'8px 10px', borderRadius:8, background:darkMode?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.02)' }}>
                        <p style={{ margin:0, fontSize:12, color:text }}>{latestNote.instructor_notes}</p>
                        <p style={{ margin:'4px 0 0', fontSize:10, color:sub }}>
                          {latestNote.flight_date && formatDateShort(latestNote.flight_date)}
                          {latestNote.instructor_name && ` · ${latestNote.instructor_name}`}
                        </p>
                      </div>
                    )}
                    {olderNotes.length > 0 && !showAllNotes && (
                      <button type="button" onClick={()=>setShowAllNotes(true)}
                        style={{ marginTop:6, padding:'4px 8px', borderRadius:8, border:'none', cursor:'pointer', background:'transparent', color:'#6366f1', fontSize:11, fontWeight:700, fontFamily:'inherit' }}>
                        {t('student_view_more_notes')}
                      </button>
                    )}
                    {olderNotes.length > 0 && showAllNotes && (
                      <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:6 }}>
                        {olderNotes.map(n => (
                          <div key={n.booking_id} style={{ padding:'8px 10px', borderRadius:8, background:darkMode?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.02)' }}>
                            <p style={{ margin:0, fontSize:12, color:text }}>{n.instructor_notes}</p>
                            <p style={{ margin:'4px 0 0', fontSize:10, color:sub }}>
                              {n.flight_date && formatDateShort(n.flight_date)}
                              {n.instructor_name && ` · ${n.instructor_name}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const BookingCard = ({ booking:b, darkMode, text, sub, cardBg, border, glassBg, slotTypeLabels, statusLabels, currentUser, t, onConfirm, onCancel, onComplete, onEdit, showActions, showComplete, showEdit }) => {
  const isMobile = useIsMobile();
  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
  const pilotInitial = b.pilot_name ? b.pilot_name.charAt(0).toUpperCase() : '?';
  const statusLabel  = statusLabels?.[b.status] || b.status;
  const showStudent  = canSeeStudentSection(b, currentUser);
  const mobileBtn = { flex:'1 1 auto', minWidth:110, justifyContent:'center' };
  return (
    <div style={{ borderRadius:20, border, background:cardBg, padding:isMobile?'16px':'18px 20px', backdropFilter:'blur(12px)', transition:'box-shadow 0.2s ease', boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 12px rgba(0,0,0,0.04)' }}>
      <div style={{ display:'flex', flexDirection:isMobile?'column':'row', alignItems:isMobile?'stretch':'flex-start', gap:isMobile?12:14 }}>
        <div style={{ display:'flex', flex:1, minWidth:0, gap:14, alignItems:'flex-start' }}>
          <div style={{ width:44, height:44, borderRadius:'50%', flexShrink:0, background:`linear-gradient(135deg, ${sc.text}33, ${sc.text}22)`, border:`2px solid ${sc.border}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:800, color:sc.text }}>
            {pilotInitial}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
              <span style={{ fontSize:15, fontWeight:700, color:text, wordBreak:'break-word' }}>{b.pilot_name||'Sin asignar'}</span>
              <span style={{ padding:'3px 10px', borderRadius:8, fontSize:10, fontWeight:700, background:sc.bg, border:`1px solid ${sc.border}`, color:sc.text }}>{statusLabel}</span>
              <span style={{ fontSize:11, fontWeight:600, color:sub, padding:'2px 8px', borderRadius:6, background:darkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.03)' }}>{slotTypeLabels?.[b.slot_type]||b.slot_type}</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:4, flexWrap:'wrap' }}>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:text, fontWeight:600 }}><Calendar style={{ width:13, height:13, color:'#3b82f6', flexShrink:0 }}/>{b.date?formatDate(b.date):'—'}</span>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:sub }}><Clock style={{ width:13, height:13, flexShrink:0 }}/>{formatTime(b.start_time)} - {formatTime(b.end_time)}</span>
            </div>
            {b.aircraft_name && <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:2 }}><Plane style={{ width:12, height:12, color:sub, flexShrink:0 }}/><span style={{ fontSize:12, color:sub, wordBreak:'break-word' }}>{b.aircraft_name}{b.registration?` (${b.registration})`:''}</span></div>}
            {b.pilot_notes && <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:6, padding:'6px 10px', borderRadius:8, background:darkMode?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.02)' }}><FileText style={{ width:12, height:12, color:sub, flexShrink:0 }}/><span style={{ fontSize:12, color:sub, fontStyle:'italic', wordBreak:'break-word' }}>{b.pilot_notes}</span></div>}
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:isMobile?'row':'column', flexWrap:isMobile?'wrap':'nowrap', gap:6, flexShrink:0, width:isMobile?'100%':'auto' }}>
          {showActions && (
            <>
              <motion.button onClick={onConfirm} whileHover={{ scale:1.03 }} whileTap={{ scale:0.95 }}
                style={{ padding:'8px 16px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', display:'flex', alignItems:'center', gap:5, boxShadow:'0 3px 12px rgba(34,197,94,0.25)', ...(isMobile?mobileBtn:{}) }}>
                <Check style={{ width:13, height:13 }}/> Confirmar
              </motion.button>
              <motion.button onClick={onCancel} whileHover={{ scale:1.03 }} whileTap={{ scale:0.95 }}
                style={{ padding:'8px 16px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, background:'rgba(239,68,68,0.1)', color:'#ef4444', display:'flex', alignItems:'center', gap:5, ...(isMobile?mobileBtn:{}) }}>
                <X style={{ width:13, height:13 }}/> Rechazar
              </motion.button>
            </>
          )}
          {showEdit && (
            <motion.button onClick={onEdit} whileHover={{ scale:1.03 }} whileTap={{ scale:0.95 }}
              style={{ padding:'8px 16px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, background:'rgba(59,130,246,0.1)', color:'#3b82f6', display:'flex', alignItems:'center', gap:5, ...(isMobile?mobileBtn:{}) }}>
              ✏️ Editar
            </motion.button>
          )}
          {showComplete && (
            <motion.button onClick={onComplete} whileHover={{ scale:1.03 }} whileTap={{ scale:0.95 }}
              style={{ padding:'8px 16px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, background:'rgba(99,102,241,0.15)', color:'#6366f1', display:'flex', alignItems:'center', gap:5, ...(isMobile?mobileBtn:{}) }}>
              <CheckCircle style={{ width:13, height:13 }}/> Completar
            </motion.button>
          )}
          {showComplete && b.status!=='cancelled' && b.status!=='completed' && (
            <motion.button onClick={onCancel} whileHover={{ scale:1.03 }} whileTap={{ scale:0.95 }}
              style={{ padding:'8px 16px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, background:'rgba(239,68,68,0.1)', color:'#ef4444', display:'flex', alignItems:'center', gap:5, ...(isMobile?mobileBtn:{}) }}>
              <XCircle style={{ width:13, height:13 }}/> Cancelar
            </motion.button>
          )}
        </div>
      </div>
      {showStudent && <StudentSection booking={b} darkMode={darkMode} text={text} sub={sub} t={t}/>}
    </div>
  );
};

export default AdminTurnos;
